import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useUpgrade } from "@/context/UpgradeContext";
import { Input } from "@/components/ui/input";
import { Check, Sparkles, Crown, Loader2, ExternalLink } from "lucide-react";

const FEATURES = [
  "Unlimited generations across all 3 tools",
  "Steam Patch Notes, Store Page & Cold Email Pitcher",
  "Full generation history storage",
  "Priority support",
];

const Pricing = () => {
  const { user, checkAuth } = useAuth();
  const { startCheckout } = useUpgrade();
  const [promo, setPromo] = useState("");
  const [busy, setBusy] = useState(false);

  const isPro = !!user?.is_pro;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      (async () => {
        await checkAuth();
        toast.message("Finishing up your subscription — Pro unlocks as soon as payment is confirmed.");
        window.history.replaceState(null, "", "/pricing");
      })();
    }
  }, [checkAuth]);

  const upgrade = () => {
    // Opens the Paddle overlay checkout; user state updates via the Paddle completion callback.
    startCheckout();
  };

  const redeemPromo = async (e) => {
    e.preventDefault();
    if (!promo.trim()) return;
    setBusy(true);
    try {
      await api.post("/billing/promo", { code: promo.trim() });
      await checkAuth();
      toast.success("Promo applied — you're a Pro Member! 🎉");
      setPromo("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid promo code.");
    } finally {
      setBusy(false);
    }
  };

  const manage = async () => {
    setBusy(true);
    try {
      const res = await api.get("/billing/portal");
      window.location.assign(res.data.url);
    } catch (e) {
      toast.error("No managed subscription. (Promo/simulation upgrades have no portal.)");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout title="Pricing" subtitle="Upgrade to unlock the full toolkit">
      <div className="max-w-lg mx-auto lg:mx-0" data-testid="pricing-page">
        <div className="relative overflow-hidden border border-teal-500/30 bg-zinc-900/30 rounded-2xl p-8">
          <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-teal-400/10 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-10 w-10 rounded-xl bg-teal-400 flex items-center justify-center">
                <Crown className="h-5 w-5 text-zinc-950" />
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-teal-300">Most Popular</p>
                <h2 className="font-heading text-2xl font-black tracking-tight text-zinc-100">IndieLaunchkit Pro</h2>
              </div>
            </div>

            <div className="flex items-baseline gap-2 mb-6">
              <span className="font-heading text-5xl font-black tracking-tighter text-zinc-100" data-testid="pro-price">$15</span>
              <span className="text-zinc-500 font-mono text-sm">/ month</span>
              <span className="text-zinc-600 font-mono text-xs">or $0 with promo</span>
            </div>

            <ul className="space-y-3 mb-8">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                  <span className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-teal-400/15 flex items-center justify-center">
                    <Check className="h-3.5 w-3.5 text-teal-300" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>

            {isPro ? (
              <div className="space-y-3" data-testid="pro-active-panel">
                <div className="flex items-center gap-2 bg-teal-400/10 border border-teal-500/30 text-teal-300 rounded-xl px-4 py-3 text-sm">
                  <Crown className="h-4 w-4" /> You're a Pro Member. Enjoy unlimited generations!
                </div>
                <button
                  onClick={manage}
                  disabled={busy}
                  data-testid="manage-subscription-btn"
                  className="w-full flex items-center justify-center gap-2 border border-zinc-700 text-zinc-200 font-medium py-3 rounded-xl hover:border-teal-500/40 hover:text-teal-300 transition-colors duration-200"
                >
                  <ExternalLink className="h-4 w-4" /> Manage Subscription
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={upgrade}
                  disabled={busy}
                  data-testid="upgrade-to-pro-btn"
                  className="w-full flex items-center justify-center gap-2 bg-teal-400 text-zinc-950 font-semibold py-3 rounded-xl hover:bg-teal-300 transition-colors duration-200 disabled:opacity-60 active:scale-[0.99]"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  Upgrade to Pro
                </button>

                <form onSubmit={redeemPromo} className="mt-4 flex gap-2" data-testid="promo-form">
                  <Input
                    value={promo}
                    onChange={(e) => setPromo(e.target.value)}
                    data-testid="promo-input"
                    placeholder="Have a promo code?"
                    className="bg-zinc-950 border-zinc-800 rounded-xl focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
                  />
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="apply-promo-btn"
                    className="shrink-0 border border-zinc-700 text-teal-300 font-medium px-5 rounded-xl hover:border-teal-500/40 hover:bg-teal-400/10 transition-colors duration-200"
                  >
                    Apply
                  </button>
                </form>
              </>
            )}

            <p className="mt-6 text-xs text-zinc-600 font-mono">
              Payments &amp; taxes handled by Paddle (Merchant of Record). Cancel anytime.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Pricing;
